import React, { useMemo } from 'react';
import { useCurrencies, useIndices } from './useMarketData';
import { CURRENCY_GROUPS, INDEX_GROUPS } from '../data/clusterDefinitions';

// --- SHARED: DATA ROW COMPONENT ---
const DataRow = ({ label, subLabel, price, change, isPositive, theme }) => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center',
    padding: '0.6rem',
    background: 'rgba(255,255,255,0.03)',
    borderRadius: '6px',
    border: '1px solid rgba(255,255,255,0.05)',
    marginBottom: '0.4rem'
  }}>
    <div style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', maxWidth: '60%' }}>
      <span style={{ fontWeight: 600, color: theme.textPrimary, fontSize: '0.85rem', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}
      </span>
      {subLabel && (
        <span style={{ fontSize: '0.65rem', color: theme.textSecondary }}>{subLabel}</span>
      )}
    </div>
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: theme.textPrimary }}>{price}</div>
      <div style={{ fontSize: '0.7rem', fontWeight: 600, color: isPositive ? '#4ade80' : '#ef4444' }}>
        {change}
      </div>
    </div>
  </div>
);

// --- SHARED: GROUP CARD ---
const ClusterCard = ({ title, items, theme, limit = 12 }) => (
  <div style={{
    background: theme.cardBg,
    borderRadius: '12px',
    padding: '1.25rem',
    border: `1px solid ${theme.border}`,
    backdropFilter: 'blur(10px)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  }}>
    <h3 style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#D4AF37', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      {title}
    </h3>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0px', flex: 1 }}>
      {items.length > 0 ? (
        items.slice(0, limit).map((item, i) => (
          <DataRow 
            key={i}
            theme={theme}
            label={item.name}
            subLabel={item.symbol}
            price={item.last_quote?.toFixed(2) || item.close?.toFixed(2)}
            change={`${item.rate >= 0 ? '+' : ''}${item.rate?.toFixed(2)}%`}
            isPositive={item.rate >= 0}
          />
        ))
      ) : (
        <span style={{ color: theme.textSecondary, fontSize: '0.8rem', padding: '1rem', textAlign: 'center' }}>
          Sem dados
        </span>
      )}
    </div>
  </div>
);

// --- LOGIC: FILTERING HELPER ---
const useFilteredData = (data, keywords) => {
  return useMemo(() => {
    if (!data || !keywords) return [];
    
    return data.filter(item => {
      const nameMatch = item.name && keywords.some(k => item.name.toLowerCase().includes(k.toLowerCase()));
      const symbolMatch = item.symbol && keywords.some(k => item.symbol.includes(k));
      return nameMatch || symbolMatch;
    }).sort((a, b) => Math.abs(b.rate || 0) - Math.abs(a.rate || 0)); // Sort by volatility
  }, [data, keywords]);
};

// --- COMPONENT 1: CURRENCY CLUSTERS ---
export const CurrencyClusters = ({ theme }) => {
  const { data } = useCurrencies();

  const brics = useFilteredData(data, CURRENCY_GROUPS.BRICS);
  const g7 = useFilteredData(data, CURRENCY_GROUPS.G7);
  const asean = useFilteredData(data, CURRENCY_GROUPS.ASEAN);
  const mercosul = useFilteredData(data, CURRENCY_GROUPS.MERCOSUL);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
      <ClusterCard title="🧱 Moedas BRICS" items={brics} theme={theme} />
      <ClusterCard title="🏦 Moedas G7" items={g7} theme={theme} />
      <ClusterCard title="🌏 Moedas ASEAN" items={asean} theme={theme} />
      <ClusterCard title="🌽 Moedas Mercosul" items={mercosul} theme={theme} />
    </div>
  );
};

// --- COMPONENT 2: INDEX CLUSTERS ---
export const IndexClusters = ({ theme }) => {
  const { data } = useIndices();

  const americas = useFilteredData(data, INDEX_GROUPS.AMERICAS);
  const europe = useFilteredData(data, INDEX_GROUPS.EUROPE);
  const asia = useFilteredData(data, INDEX_GROUPS.ASIA);
  const brics = useFilteredData(data, INDEX_GROUPS.BRICS);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
      <ClusterCard title="🌎 Índices Américas" items={americas} theme={theme} />
      <ClusterCard title="🇪🇺 Índices Europa" items={europe} theme={theme} />
      <ClusterCard title="⛩️ Índices Ásia" items={asia} theme={theme} />
      <ClusterCard title="🧱 Índices BRICS" items={brics} theme={theme} />
    </div>
  );
};